import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./api";
import type { DraftSummary, ExpandedKey, ProfileSummary, ProjectSummary, SessionSummary, SidebarView, User } from "./types";
import { parseDeepLink, type DeepLink } from "./deepLink";
import { disablePush } from "./push";
import { Login } from "./views/Login";
import { Sidebar } from "./views/Sidebar";
import { Chat } from "./views/Chat";
import { filingTargets } from "./views/filing";
import { ProfileConfig } from "./views/ProfileConfig";
import { SkillManager } from "./views/SkillManager";
import { Loops } from "./views/Loops";
import { Memory } from "./views/Memory";
import { Notes } from "./views/Notes";
import { Usage } from "./views/Usage";
import { NewProject, Project } from "./views/Project";
import { ResearchPage, type ResearchLink } from "./views/ResearchPage";
import { RESEARCH_OPEN } from "./components/Research";
import { Schedules } from "./views/Schedules";
import { Tasks } from "./views/Tasks";
import { MenuIcon } from "./icons";
import { useIsNarrow } from "./viewport";
import { Rail, useNavBadges, type Page } from "./views/Rail";

const LAST_SESSION_KEY = "hifi.lastSession";
const THEME_KEY = "hifi.theme";
const SIDEBAR_KEY = "hifi.sidebar";
type Theme = "dark" | "light";

/**
 * The stored choice, and failing that whatever the machine is already set to.
 *
 * Opening dark on a device set to light ignores a preference the person has already expressed
 * once, system-wide; only an explicit click here should override it. `"day"` is the name this
 * setting shipped under, so anyone who picked it keeps their choice.
 */
function savedTheme(): Theme {
	const stored = localStorage.getItem(THEME_KEY);
	if (stored === "light" || stored === "day") return "light";
	if (stored === "dark") return "dark";
	return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

/**
 * Only a 401 means "not logged in".
 *
 * The session cookie lives for 30 days and the server keeps its half in sqlite, so a
 * restart never invalidates it — but the few seconds where the backend is down look like
 * a failed request. Treating that as logged-out is what made every restart end in a
 * password prompt, and typing the password "fixed" it, which hid the real cause.
 */
type AuthState =
	| { state: "checking" }
	| { state: "offline"; attempts: number }
	| { state: "out" }
	| { state: "in"; user: User };

const RETRY_MS = 2000;

export function App() {
	const [theme, setTheme] = useState<Theme>(savedTheme);
	const [auth, setAuth] = useState<AuthState>({ state: "checking" });
	const user = auth.state === "in" ? auth.user : null;
	const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
	const [broken, setBroken] = useState<Array<{ name: string }>>([]);
	const [drafts, setDrafts] = useState<DraftSummary[]>([]);
	const [enablingDraft, setEnablingDraft] = useState<string | null>(null);
	const [justEnabled, setJustEnabled] = useState<string | null>(null);
	const [sessions, setSessions] = useState<SessionSummary[]>([]);
	const [projects, setProjects] = useState<ProjectSummary[]>([]);
	/* The person's own sidebar: stars, categories, open folds and recent uses. It is fetched with the
	   sessions, so it changes when they do and never because a conversation was merely opened. */
	const [sidebarView, setSidebarView] = useState<SidebarView | null>(null);
	/* Every account, for naming the people a session is shared with. */
	const [users, setUsers] = useState<User[]>([]);
	const [currentId, setCurrentId] = useState<string | null>(null);
	/* The message a branch was cut before, handed to that branch's composer once. */
	const [branchDraft, setBranchDraft] = useState<{ id: string; text: string } | null>(null);
	/** A delegated session opened from its call's card: read-only, never in the list, returns to its parent. */
	const [delegated, setDelegated] = useState<SessionSummary | null>(null);
	/* The project page being shown, and the profile a new project is being created for. A project
	   is not a session (ADR-0004): it gets a page like loops do, reached from the sidebar. */
	const [projectId, setProjectId] = useState<string | null>(null);
	const [newProjectFor, setNewProjectFor] = useState<string | null>(null);
	const [drawer, setDrawer] = useState(false);
	/**
	 * The drawer is a history entry, so the phone's back gesture closes it.
	 *
	 * Standalone from the home screen there is no address bar, so Android's back button pops a
	 * stack with one entry in it and quits the app -- from what is, on a phone, the most frequent
	 * gesture in the product. One entry is enough to absorb that; the rest of the app's state is
	 * still not addressable (see docs/ui-redesign, phase 7).
	 */
	useEffect(() => {
		if (!drawer) return;
		history.pushState({ drawer: true }, "");
		const onPop = () => setDrawer(false);
		addEventListener("popstate", onPop);
		return () => {
			removeEventListener("popstate", onPop);
			// Closed by tapping something rather than by going back: drop the entry we added.
			if (history.state?.drawer) history.back();
		};
	}, [drawer]);
	/* On a wide screen the sidebar folds away instead of sliding in. The same buttons drive both,
	   and the fold outlives a reload. */
	const narrow = useIsNarrow();
	const [sidebarHidden, setSidebarHidden] = useState(() => localStorage.getItem(SIDEBAR_KEY) === "hidden");
	useEffect(() => localStorage.setItem(SIDEBAR_KEY, sidebarHidden ? "hidden" : "shown"), [sidebarHidden]);
	const openSidebar = () => (narrow ? setDrawer(true) : setSidebarHidden(false));
	const closeSidebar = () => (narrow ? setDrawer(false) : setSidebarHidden(true));
	const [error, setError] = useState<string | null>(null);
	/* Which profile's panel is open, and which of the two panels it is. They are held here rather
	   than in the sidebar because the skill editor swaps places with the config panel -- stacking
	   one modal on another is worse than replacing it -- and because both outlive the mobile drawer
	   that opened them. */
	const [panel, setPanel] = useState<{ profile: string; view: "config" | "skills" } | null>(null);
	/* Loops and schedules get pages of their own rather than a place in the session list: neither
	   is a session (ADR-0002, ADR-0005), and what they open is reached through them. */
	const [view, setView] = useState<Page>("chat");
	/* The loop or schedule a notification pointed at, handed to its page to select. */
	const [loopLink, setLoopLink] = useState<string | null>(null);
	const [scheduleLink, setScheduleLink] = useState<string | null>(null);
	/* The research scope and record a link pointed at, handed to the research page. */
	const [researchLink, setResearchLink] = useState<ResearchLink | null>(null);
	const badges = useNavBadges(Boolean(user), view);
	/* The task page opens on the deployment when a loop sends the person there to approve it. */
	const [tasksOnDeploy, setTasksOnDeploy] = useState(false);
	useEffect(() => {
		if (view !== "tasks") setTasksOnDeploy(false);
	}, [view]);

	useEffect(() => {
		document.documentElement.dataset.theme = theme;
		localStorage.setItem(THEME_KEY, theme);
		// The browser chrome and the app are the same surface on a phone; a dark status bar over
		// a linen page is the most visible thing left saying the light theme was an afterthought.
		const meta = document.querySelector('meta[name="theme-color"]');
		if (meta) meta.setAttribute("content", theme === "light" ? "#ffffff" : "#1f2c33");
	}, [theme]);

	/** Resolves true once the answer is authoritative; false means "ask again later". */
	const checkAuth = useCallback(async (attempts: number): Promise<boolean> => {
		try {
			const { user } = await api.me();
			setAuth({ state: "in", user });
			return true;
		} catch (err) {
			if (err instanceof ApiError && err.status === 401) {
				setAuth({ state: "out" });
				return true;
			}
			setAuth({ state: "offline", attempts });
			return false;
		}
	}, []);

	useEffect(() => {
		if (auth.state === "in" || auth.state === "out") return;
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout>;
		const attempts = auth.state === "offline" ? auth.attempts : 0;
		void (async () => {
			const settled = await checkAuth(attempts + 1);
			if (!settled && !cancelled) timer = setTimeout(() => setAuth({ state: "offline", attempts: attempts + 1 }), RETRY_MS);
		})();
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [auth, checkAuth]);

	// 404 means no profile is the builder, which is the ordinary case, not an error.
	const refreshDrafts = useCallback(async () => {
		try {
			setDrafts((await api.drafts()).drafts);
		} catch (err) {
			if (!(err instanceof ApiError && err.status === 404)) return;
			setDrafts([]);
		}
	}, []);

	const refreshProfiles = useCallback(async () => {
		try {
			const [p] = await Promise.all([api.profiles(), refreshDrafts()]);
			setProfiles(p.profiles);
			setBroken(p.broken);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "加载 profile 失败");
		}
	}, [refreshDrafts]);

	// The builder writes drafts from inside a conversation, and nothing tells the sidebar. A slow
	// poll is enough: a draft is minutes in the writing, and nobody enables one mid-sentence.
	useEffect(() => {
		if (!user) return;
		const timer = setInterval(() => void refreshDrafts(), 20_000);
		return () => clearInterval(timer);
	}, [user, refreshDrafts]);

	const enableDraft = useCallback(
		async (name: string, owner: "me" | "shared" | "keep") => {
			setEnablingDraft(name);
			try {
				await api.enableDraft(name, owner);
				setJustEnabled(name);
				await refreshProfiles();
			} catch (err) {
				setError(err instanceof ApiError ? err.message : "启用失败");
			} finally {
				setEnablingDraft(null);
			}
		},
		[refreshProfiles],
	);

	/** null means the list could not be fetched -- not that there are no sessions. */
	const refreshSessions = useCallback(async () => {
		try {
			const [{ sessions }] = await Promise.all([
				api.sessions(),
				// A view that does not load leaves the sidebar as it was.
				api.sidebar().then(setSidebarView, () => {}),
			]);
			setSessions(sessions);
			return sessions;
		} catch {
			return null;
		}
	}, []);

	const refreshProjects = useCallback(async () => {
		try {
			const { projects } = await api.projects();
			setProjects(projects);
		} catch {
			// The sidebar just shows the sessions loose until the next refresh succeeds.
		}
	}, []);

	/** Opening a chat, from wherever, or the open one with null. It closes a delegated transcript, which renders ahead of it. */
	const showChat = useCallback((id: string | null) => {
		setDelegated(null);
		if (id) setCurrentId(id);
		setView("chat");
	}, []);

	/** Opens what a notification points at. A session a schedule just opened is newer than the list, so the list comes first. */
	const followLink = useCallback(
		async (link: DeepLink) => {
			if (link.kind === "session") {
				await refreshSessions();
				showChat(link.id);
			} else if (link.kind === "research") {
				setDelegated(null);
				setResearchLink({ scope: link.scope, record: link.record, view: link.view, at: Date.now() });
				setView("research");
			} else if (link.kind === "loop") {
				setLoopLink(link.id);
				setView("loops");
			} else {
				setScheduleLink(link.id);
				setView("schedules");
			}
			setDrawer(false);
		},
		[refreshSessions],
	);

	/** A session in the list opens in place; one that is not is a delegated job, fetched and opened read-only. */
	const openSession = useCallback(
		async (id: string) => {
			if (sessions.some((s) => s.id === id)) {
				showChat(id);
				return;
			}
			try {
				const { session } = await api.session(id);
				if (session.createdBy === "delegate") setDelegated(session);
			} catch (err) {
				setError(err instanceof ApiError ? err.message : "无法打开此对话");
			}
		},
		[sessions],
	);

	useEffect(() => {
		if (!user) return;
		void (async () => {
			// Independent calls, so fire them together — one fewer round trip, which is worth
			// a real fraction of a second when the server is on another continent.
			const [, fetched] = await Promise.all([
				refreshProfiles(),
				refreshSessions(),
				refreshProjects(),
				api
					.users()
					.then((r) => setUsers(r.users))
					.catch(() => {}),
			]);
			// refreshSessions reports null when it could not reach the server; there is simply
			// nothing to restore in that case.
			const list = fetched ?? [];
			// Opened from a notification: go where it points, and take it out of the address bar so
			// a reload does not go there again.
			const link = parseDeepLink(window.location.href);
			if (link) window.history.replaceState(null, "", link.kind === "research" ? "/" : window.location.pathname);
			// Otherwise come back to the last conversation after a refresh or a device switch.
			const last = localStorage.getItem(LAST_SESSION_KEY);
			const linked = link?.kind === "session" ? list.find((s) => s.id === link.id) : undefined;
			const target = linked ?? list.find((s) => s.id === last) ?? list[0];
			if (target) setCurrentId(target.id);
			if (link && link.kind !== "session") void followLink(link);
		})();
	}, [user, refreshProfiles, refreshSessions, refreshProjects, followLink]);

	// A research link in a chat view or on a project page opens the research page in place.
	useEffect(() => {
		if (!user) return;
		const onOpen = (event: Event) => {
			const link = parseDeepLink(String((event as CustomEvent<unknown>).detail));
			if (link?.kind !== "research") return;
			event.preventDefault();
			void followLink(link);
		};
		window.addEventListener(RESEARCH_OPEN, onOpen);
		return () => window.removeEventListener(RESEARCH_OPEN, onOpen);
	}, [user, followLink]);

	// A notification tapped while the app is already open arrives as a message from the service worker.
	useEffect(() => {
		if (!user || !("serviceWorker" in navigator)) return;
		const onMessage = (event: MessageEvent) => {
			const data = event.data as { type?: string; url?: string } | null;
			const link = data?.type === "open" && data.url ? parseDeepLink(data.url) : null;
			if (link) void followLink(link);
		};
		navigator.serviceWorker.addEventListener("message", onMessage);
		return () => navigator.serviceWorker.removeEventListener("message", onMessage);
	}, [user, followLink]);

	useEffect(() => {
		if (currentId) localStorage.setItem(LAST_SESSION_KEY, currentId);
	}, [currentId]);

	async function newSession(profile: string, opts: { projectId?: string; anonymous?: boolean } = {}) {
		try {
			const { session } = await api.createSession(profile, opts);
			setSessions((v) => [session, ...v]);
			showChat(session.id);
			setDrawer(false);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "创建对话失败");
		}
	}

	/** A branch is opened like a new session, with the cut-off message waiting in the composer. */
	async function branch(sessionId: string, entryId: string) {
		try {
			const { session, draft } = await api.branchSession(sessionId, entryId);
			setSessions((v) => [session, ...v]);
			setBranchDraft(draft ? { id: session.id, text: draft } : null);
			showChat(session.id);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "分支失败");
		}
	}

	function openProject(id: string) {
		setDelegated(null);
		setProjectId(id);
		setView("project");
		setDrawer(false);
	}

	/**
	 * A just-created project on a profile with an opening starts its first conversation at once,
	 * with the opening sent on the owner's behalf so the agent speaks first. Without one, the
	 * project page opens as usual.
	 */
	async function openNewProject(project: ProjectSummary) {
		const opening = profiles.find((p) => p.name === project.profile)?.projectOpening;
		if (!opening) return openProject(project.id);
		try {
			const { session } = await api.createSession(project.profile, { projectId: project.id });
			setSessions((v) => [session, ...v]);
			showChat(session.id);
			setDrawer(false);
			await api.send(session.id, opening);
			await refreshSessions();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "创建对话失败");
		}
	}

	/**
	 * Archiving is one tap, and nothing in the web UI brings a session back, so it asks first --
	 * every other destructive action here already does.
	 *
	 * A failure is reported rather than swallowed, and a refresh that could not reach the server
	 * says so instead of answering []: treating that as "no sessions" closes whatever you were
	 * reading, which offline looks exactly like the app losing your conversation.
	 */
	async function archive(id: string) {
		const title = sessions.find((s) => s.id === id)?.title;
		if (!confirm(`归档${title ? `「${title}」` : "此对话"}？归档后只能在服务端恢复。`)) return;
		try {
			await api.archiveSession(id);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "归档失败");
			return;
		}
		const list = await refreshSessions();
		if (list && currentId === id) setCurrentId(list[0]?.id ?? null);
	}

	/** Files a conversation under a project; the list shows it there once the server has it. */
	async function fileSession(id: string, projectId: string) {
		try {
			await api.fileSession(id, projectId);
		} catch {
			setError("移入失败");
			return;
		}
		await refreshSessions();
	}

	/**
	 * One change to the person's sidebar. The server answers with their whole view, which replaces the
	 * one shown; false when the change did not go through, after saying why.
	 */
	async function changeSidebar(change: Promise<SidebarView>, failed: string): Promise<boolean> {
		try {
			setSidebarView(await change);
			return true;
		} catch (err) {
			setError(err instanceof ApiError ? err.message : failed);
			return false;
		}
	}

	/**
	 * A fold opens or closes on screen at once, then the server remembers it. Only the view's open
	 * folds change: taking the server's whole answer would move the ranking on a mere toggle, and
	 * without a view there is nothing to change on screen.
	 */
	async function toggleFold(key: ExpandedKey, open: boolean) {
		setSidebarView((v) => v && { ...v, expanded: [...v.expanded.filter((k) => k !== key), ...(open ? [key] : [])] });
		try {
			await (open ? api.openFold(key) : api.closeFold(key));
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "展开状态保存失败");
		}
	}

	async function logout() {
		// While still logged in: the server removes this device only for the person it belongs to,
		// so a shared phone stops receiving the other person's pushes.
		await disablePush().catch(() => {});
		await api.logout().catch(() => {});
		setAuth({ state: "out" });
		setSessions([]);
		setSidebarView(null);
		setCurrentId(null);
	}

	if (auth.state === "checking") return <div className="boot">加载中…</div>;
	if (auth.state === "offline") {
		return (
			<div className="boot offline">
				<p>无法连接服务端</p>
				{auth.attempts > 1 && <p className="hint">已重试 {auth.attempts} 次</p>}
				<button onClick={() => setAuth({ state: "checking" })}>立即重试</button>
			</div>
		);
	}
	if (auth.state === "out") return <Login onLogin={(u) => setAuth({ state: "in", user: u })} />;

	const current = sessions.find((s) => s.id === currentId) ?? null;
	// A reload can drop the profile the panel was opened for; closing beats rendering a blank one.
	const panelProfile = panel ? (profiles.find((p) => p.name === panel.profile) ?? null) : null;
	// A project's research records and map open on the research page when its profile has the research tools.
	const shownProject = projects.find((p) => p.id === projectId);
	const projectResearch =
		shownProject && shownProject.owner === auth.user.id && profiles.some((p) => p.name === shownProject.profile && p.tools.includes("research"))
			? (view?: "map") => void followLink({ kind: "research", scope: { kind: "project", projectId: shownProject.id }, ...(view ? { view } : {}) })
			: undefined;

	return (
		<div className={`layout${drawer ? " drawer-open" : ""}${sidebarHidden ? " sidebar-hidden" : ""}${view === "chat" || view === "project" ? " with-panel" : ""}`}>
			{!narrow && (
				<Rail
					page={view}
					badges={badges}
					userName={auth.user.name}
					theme={theme}
					onOpenChat={() => showChat(null)}
					onOpenPage={(page) => {
						setDelegated(null);
						setView(page);
					}}
					onThemeToggle={() => setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"))}
					onLogout={() => void logout()}
				/>
			)}
			<Sidebar
				badges={badges}
				user={auth.user}
				users={users}
				profiles={profiles}
				broken={broken}
				drafts={drafts}
				enablingDraft={enablingDraft}
				justEnabled={justEnabled}
				onEnableDraft={(name, owner) => void enableDraft(name, owner)}
				sessions={sessions}
				projects={projects}
				view={sidebarView}
				onToggleStar={(profile, starred) =>
					void changeSidebar(starred ? api.starProfile(profile) : api.unstarProfile(profile), "星标保存失败")
				}
				onToggleFold={(key, open) => void toggleFold(key, open)}
				onCreateCategory={(name) => changeSidebar(api.createCategory(name).then((r) => r.view), "分类创建失败")}
				onRenameCategory={(id, name) => changeSidebar(api.renameCategory(id, name), "分类重命名失败")}
				onRemoveCategory={(id) => changeSidebar(api.removeCategory(id), "分类删除失败")}
				onPlaceProfile={(profile, categoryId) => changeSidebar(api.placeProfile(profile, categoryId), "移动分类失败")}
				currentSessionId={currentId}
				currentProjectId={projectId}
				theme={theme}
				onThemeToggle={() => setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"))}
				onNewSession={(p, project) => void newSession(p, { projectId: project })}
				onNewAnonymousSession={(p) => void newSession(p, { anonymous: true })}
				onNewProject={(p) => {
					setNewProjectFor(p);
					setDrawer(false);
				}}
				onOpenProject={openProject}
				onOpenConfig={(p) => {
					setPanel({ profile: p, view: "config" });
					setDrawer(false);
				}}
				onOpenSession={(id) => {
					showChat(id);
					setDrawer(false);
				}}
				page={view}
				onOpenPage={(page) => {
					setDelegated(null);
					setView(page);
					setDrawer(false);
				}}
				onArchiveSession={(id) => void archive(id)}
				onFileSession={(id, project) => void fileSession(id, project)}
				onLogout={() => void logout()}
				onClose={closeSidebar}
			/>
			{error && (
				<p className="banner error floating">
					{error}
					<button className="banner-close" title="关闭" onClick={() => setError(null)}>
						×
					</button>
				</p>
			)}
			{panel?.view === "config" && panelProfile && (
				<ProfileConfig
					profile={panelProfile}
					onClose={() => setPanel(null)}
					onManageSkills={() => setPanel({ profile: panel.profile, view: "skills" })}
					onChanged={() => void refreshProfiles()}
				/>
			)}
			{panel?.view === "skills" && (
				<SkillManager
					profile={panel.profile}
					onClose={() => setPanel({ profile: panel.profile, view: "config" })}
					onChanged={() => void refreshProfiles()}
				/>
			)}
			{newProjectFor && (
				<NewProject
					profile={newProjectFor}
					onClose={() => setNewProjectFor(null)}
					onCreated={(project) => {
						setNewProjectFor(null);
						setProjects((v) => [project, ...v]);
						void openNewProject(project);
					}}
				/>
			)}
			{view === "loops" ? (
				<Loops
					profiles={profiles}
					openId={loopLink}
					onMenu={openSidebar}
					onOpenDeploy={() => {
						setTasksOnDeploy(true);
						setView("tasks");
					}}
				/>
			) : view === "schedules" ? (
				<Schedules profiles={profiles} openId={scheduleLink} onMenu={openSidebar} onOpenSession={(id) => void followLink({ kind: "session", id })} />
			) : view === "tasks" ? (
				<Tasks projects={projects} onMenu={openSidebar} onOpenSession={(id) => void followLink({ kind: "session", id })} openDeploy={tasksOnDeploy} />
			) : view === "memory" ? (
				<Memory onMenu={openSidebar} />
			) : view === "notes" ? (
				<Notes onMenu={openSidebar} />
			) : view === "usage" ? (
				<Usage onMenu={openSidebar} />
			) : view === "research" ? (
				<ResearchPage me={auth.user.id} projects={projects} profiles={profiles} link={researchLink} onMenu={openSidebar} />
			) : view === "project" && projectId ? (
				<Project
					key={projectId}
					id={projectId}
					onMenu={openSidebar}
					onOpenSession={showChat}
					onNewSession={(p, project) => void newSession(p, { projectId: project })}
					onChanged={() => void refreshProjects()}
					onOpenResearch={projectResearch}
				/>
			) : delegated ? (
				<Chat
					key={delegated.id}
					session={delegated}
					profile={profiles.find((p) => p.name === delegated.profile)}
					sessions={sessions}
					readOnly
					onOpenSession={(id) => void openSession(id)}
					onMenu={openSidebar}
					onBack={() => setDelegated(null)}
					onRunEnded={() => {}}
				/>
			) : current ? (
				<Chat
					key={current.id}
					session={current}
					profile={profiles.find((p) => p.name === current.profile)}
					projectTitle={current.projectId ? projects.find((p) => p.id === current.projectId)?.title : undefined}
					fileTo={filingTargets(current, auth.user.id, projects)}
					onFile={(project) => void fileSession(current.id, project)}
					me={auth.user}
					users={users}
					sessions={sessions}
					onBranch={(entryId) => void branch(current.id, entryId)}
					onOpenSession={(id) => void openSession(id)}
					onOpenLoop={(id) => void followLink({ kind: "loop", id })}
					initialDraft={branchDraft?.id === current.id ? branchDraft.text : undefined}
					onMenu={openSidebar}
					onRunEnded={() => {
						void refreshSessions();
						void refreshProjects();
					}}
					// The server titles a conversation on accepting its first message; until the list has
					// that title, the conversation drops out of history as soon as it is left.
					onSent={current.title === null ? () => void refreshSessions() : undefined}
					onRenamed={() => void refreshSessions()}
				/>
			) : (
				<main className="chat empty">
					<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={openSidebar}>
						<MenuIcon size={22} />
					</button>
				</main>
			)}
		</div>
	);
}
